import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { RefreshCw, Plus, Edit2, Trash2, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  PageHeader,
  PageSection,
  Table,
  Tabs,
} from '@/components';
import type { ChannelType } from '@/services/channelsService';
import { listTemplates, deleteTemplate, syncMetaTemplates, type Template } from '@/services/templatesService';
import { TemplateFormModal } from '@/components/templates/TemplateFormModal';

type ChannelTab = 'whatsapp' | 'webchat';

export function TemplatesByChannel() {
  const [tab, setTab] = useState<ChannelTab>('whatsapp');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const queryClient = useQueryClient();

  const channel: ChannelType = tab;

  const { data: response, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['templates', channel],
    queryFn: () => listTemplates({ channel }),
  });

  const templates = response?.data ?? [];

  const syncMutation = useMutation({
    mutationFn: syncMetaTemplates,
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      toast.success('Templates sincronizados com Meta!');
    },
    onError: () => {
      toast.error('Erro ao sincronizar templates');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
      toast.success('Template deletado');
    },
    onError: () => {
      toast.error('Erro ao deletar template');
    },
  });

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja deletar este template?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSync = () => {
    syncMutation.mutate();
  };

  const getStatusBadge = (template: Template) => {
    const metaStatus = (template as any).meta_status;
    
    if (channel === 'whatsapp' && metaStatus) {
      switch (metaStatus) {
        case 'APPROVED':
          return <Badge variant="success"><CheckCircle size={14} className="inline mr-1" />Aprovado</Badge>;
        case 'PENDING':
          return <Badge variant="warning"><Clock size={14} className="inline mr-1" />Pendente</Badge>;
        case 'REJECTED':
          return <Badge variant="error"><XCircle size={14} className="inline mr-1" />Rejeitado</Badge>;
        default:
          return <Badge variant="neutral">{metaStatus}</Badge>;
      }
    }
    
    switch (template.status) {
      case 'active':
        return <Badge variant="success">Ativo</Badge>;
      case 'draft':
        return <Badge variant="warning">Rascunho</Badge>;
      case 'archived':
        return <Badge variant="neutral">Arquivado</Badge>;
      default:
        return <Badge variant="neutral">{template.status}</Badge>;
    }
  };

  return (
    <Page>
      <PageHeader
        title="Templates por Canal"
        description="Gerencie templates por canal (WhatsApp, Web Chat e futuros canais)."
        right={
          <div className="flex gap-2">
            {tab === 'whatsapp' && (
              <Button 
                variant="secondary" 
                onClick={handleSync}
                disabled={syncMutation.isLoading}
              >
                <RefreshCw size={16} className={syncMutation.isLoading ? 'animate-spin' : ''} />
                Sincronizar Meta
              </Button>
            )}
            <Button variant="primary" onClick={handleCreate}>
              <Plus size={16} />
              Novo Template
            </Button>
          </div>
        }
      />

      <PageSection>
        <Tabs<ChannelTab>
          value={tab}
          onChange={setTab}
          options={[
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'webchat', label: 'Web Chat' },
          ]}
        />

        <div style={{ height: 16 }} />

        {tab === 'whatsapp' && (
          <Card>
            <CardBody>
              <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                <AlertCircle size={20} className="text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">Templates WhatsApp</p>
                  <p className="text-xs text-blue-700">
                    Templates WhatsApp precisam ser aprovados pela Meta. Use "Sincronizar Meta" para buscar templates já cadastrados.
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        <div style={{ height: 16 }} />

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-semibold">Lista de templates</div>
              <Badge variant="neutral">Canal: {tab}</Badge>
            </div>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <LoadingState title="Carregando templates" description="Buscando templates do canal selecionado." />
            ) : isError ? (
              <ErrorState
                title="Falha ao carregar templates"
                description={
                  error instanceof Error ? error.message : 'Não foi possível carregar os templates.'
                }
                onRetry={() => refetch()}
              />
            ) : templates.length === 0 ? (
              <EmptyState
                title="Nenhum template encontrado"
                description="Crie um template para este canal para começar." 
                action={<Button variant="secondary" onClick={handleCreate}>Novo Template</Button>}
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Canal</th>
                    <th>Status</th>
                    <th>Linguagem</th>
                    <th>Atualizado</th>
                    <th style={{ width: 140 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id}>
                      <td className="font-medium">{t.name}</td>
                      <td>
                        <Badge variant="neutral">{t.channel}</Badge>
                      </td>
                      <td>{getStatusBadge(t)}</td>
                      <td className="text-gray-600">{t.language}</td>
                      <td className="text-gray-500">{new Date(t.updated_at).toLocaleDateString('pt-BR')}</td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(t)}
                            className="text-blue-600 hover:text-blue-700"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(t.id)}
                            className="text-red-600 hover:text-red-700"
                            title="Deletar"
                            disabled={deleteMutation.isLoading}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </PageSection>

      {isModalOpen && (
        <TemplateFormModal
          template={editingTemplate}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            queryClient.invalidateQueries(['templates']);
          }}
        />
      )}
    </Page>
  );
}
